<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { SplashView, LayoutView, LoginView } from '@/components/views';
import { useThemeStore, useToastStore, useUserStoreRef } from '@/stores';
import { useAccessValidator } from './composeables/accessValidator';
import { useClipboardHandler } from './composeables/clipboard/clipboardhandler';
import { useDismissedMessages } from './composeables/messages/dismissedMessages';
import ProductionClientView from './components/views/Production/ProductionClientView.vue';
import { GetAppsettingError, useWindowTitleSetter } from './composeables';
import ToastContainer from './components/toast/ToastContainer.vue';
import { initGlobalSearch } from './globalsearch/globalsearch';
import {
    registerSignalrResendMissingMessagesAlertCallback,
    registerSequenceNumberRegressionAlertCallback,
} from './signalr/signalrSubscriptionHandler.ts';

const loaded = ref(false);
const { user } = useUserStoreRef();

// Watch for changes in the user object and validate the access
watch(user, (newUser) => (newUser != null ? useAccessValidator().SetUser(newUser) : null));

useClipboardHandler();
useDismissedMessages();
useWindowTitleSetter();
useThemeStore();
initGlobalSearch();

// Check for signalR sequence number regression
registerSequenceNumberRegressionAlertCallback(() => {
    useToastStore().addWarning('signalr.sequence_number_regression_detected');
});

registerSignalrResendMissingMessagesAlertCallback(() => {
    useToastStore().addWarning('signalr.resend_missing_messages_detected');
});

/**
 * This is just a temporary solution to check if we are on the production screen.
 * The idea is to have this screen in the new Live, but then we need to set up the project.
 * This is just to test concepts. This should be removed when the EQ Live project is set up. and the production view is implemented.
 */
const isProductionScreen = document.location.href.includes('production=true');

if (GetAppsettingError() != null) {
    useToastStore().addWarning(GetAppsettingError()?.message ?? 'There was an error getting the appsettings');
}

onMounted(() => {
    // We need to find out if it is firefox, chrome or edge and add a class to the body for css adjustments
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Firefox') > -1) {
        document.body.classList.add('firefox-browser');
    } else if (userAgent.indexOf('Edg') > -1) {
        document.body.classList.add('edge-browser');
    } else if (userAgent.indexOf('Chrome') > -1) {
        document.body.classList.add('chrome-browser');
    }
});
</script>

<template>
    <ProductionClientView v-if="isProductionScreen" />
    <template v-else>
        <SplashView
            @loaded="(v: boolean) => (loaded = v)"
            v-if="!loaded"
        />
        <template v-if="loaded">
            <LayoutView v-if="user != null" />
            <LoginView v-if="user == null" />
        </template>

        <Teleport to="body">
            <ToastContainer v-if="user == null"></ToastContainer>
        </Teleport>
    </template>
</template>
